<script lang="ts" setup>
import AcumaticaLoginModal from '@/components/modals/AcumaticaLoginModal.vue';
import { createSalesOrder, loginToAcumatica } from '@/services/mintechAPI';
import { useWebOrderManager } from '@/services/webOrderManager';
import { SalesOrder } from '@/types/salesOrder';
import { useFormatters } from '@/util/formatters';
import { onMounted, Ref, ref, watch } from 'vue';

//--------------------------------------------------
// Service Initialization
//--------------------------------------------------
const wom = useWebOrderManager();
const { formatCurrency } = useFormatters();

//--------------------------------------------------
// Reactive State
//--------------------------------------------------
const baid: Ref<string> = ref('');
const poNumber: Ref<string> = ref('');
const salesOrderDescription: Ref<string> = ref('');

const isUIBusy: Ref<boolean> = ref(false);

const displayLoginForm: Ref<boolean> = ref(false);
const loginOperation: Ref<'ready' | 'active' | 'failed'> = ref('ready');

function closeSlideOutModal() {
	if (isUIBusy.value) return;
	wom.clearSelectedWebOrder();
}

function closeLoginForm() {
	if (isUIBusy.value) return;
	displayLoginForm.value = false;
	loginOperation.value = 'ready';
}

//--------------------------------------------------
// Create Sales Order
//--------------------------------------------------
async function createSO() {
	isUIBusy.value = false;
	displayLoginForm.value = false;

	if (!wom.selectedWebOrder.value) return;

	if (!baid.value || !poNumber.value) {
		alert("'BAID' and 'PO Number' cannot be empty");
	}

	isUIBusy.value = true;

	const salesOrder: SalesOrder = {
		baid: baid.value,
		poNumber: poNumber.value,
		description: salesOrderDescription.value,
		items: wom.selectedWebOrder.value.lineItems
	};

	try {
		const { salesOrderCreated, userVerified } = await createSalesOrder(salesOrder);
		if (salesOrderCreated) {
			alert('Sales Order Created');
			return;
		} else if (userVerified && !salesOrderCreated) {
			alert('Request cannot be processed: sales order creation failed');
			return;
		} else if (!userVerified) {
			alert('Acumatica session invalid. Please re-authenticate.');
			displayLoginForm.value = true;
		}
	} catch {
		alert('Error creating sales order');
	} finally {
		isUIBusy.value = false;
	}
}

//--------------------------------------------------
// Login To Acumatica
//--------------------------------------------------
async function loginToAC(username: string, password: string) {
	isUIBusy.value = false;
	loginOperation.value = 'ready';

	if (!username || !password) {
		alert('Request cannot be processed: missing username or password.');
		return;
	}

	try {
		isUIBusy.value = true;
		loginOperation.value = 'active';
		const loginSuccess = await loginToAcumatica(username, password);

		if (loginSuccess) {
			alert('Sucess! Try creating the sales order again.');
			closeLoginForm();
		} else {
			alert('Failed! Please check your credentials and try again');
			loginOperation.value = 'failed';
		}
	} catch {
		alert('Request cannot be processed: network error');
	} finally {
		isUIBusy.value = false;
	}
}

//--------------------------------------------------
// Watchers
//--------------------------------------------------
watch(
	() => wom.selectedWebOrder.value,
	(newValue) => {
		if (!newValue) {
			salesOrderDescription.value = '';
			baid.value = '';
			poNumber.value = '';
			return;
		}
		salesOrderDescription.value = `WebOrder${newValue.id}`;
	}
);

//--------------------------------------------------
// Life Cycle Hooks
//--------------------------------------------------
onMounted(async () => {
	await wom.fetchWebOrders();
});
</script>

<template>
	<div class="web-orders-view">
		<!-- Title and Search Bar -->
		<div class="web-orders-view__header">
			<div class="header__titles">
				<h1 class="fira aliceblue">Web Orders</h1>
				<p class="fira aliceblue semi-large">Shopify Web Orders</p>
			</div>
			<input v-model="wom.searchQuery.value" placeholder="Search..." />
		</div>

		<!-- Main Content -->
		<div class="web-orders-view__content">
			<!-- Tables -->
			<div class="web-orders-view__tables">
				<!-- Table Header and Table Body -->
				<div class="tables__table-header">
					<table class="main-table">
						<colgroup>
							<col style="width: 75px" />
							<col style="min-width: 200px; width: auto" />
							<col style="width: 200px" />
							<col style="width: 200px" />
							<col style="width: 200px" />
							<col style="width: 200px" />
						</colgroup>
						<thead>
							<tr>
								<th v-for="heading in wom.tableHeadings" :key="heading">
									{{ heading }}
								</th>
							</tr>
						</thead>
					</table>
				</div>

				<div class="tables__main-table">
					<table class="main-table">
						<colgroup>
							<col style="width: 75px" />
							<col style="width: auto" />
							<col style="width: 200px" />
							<col style="width: 200px" />
							<col style="width: 200px" />
							<col style="width: 200px" />
						</colgroup>
						<tbody>
							<tr
								v-for="row in wom.rowData.value"
								:key="row[0]"
								:class="{ selected: row[0] === wom.selectedWebOrderId.value }"
								@click="wom.selectWebOrder(row[0])"
							>
								<td v-for="cell in row" :key="cell[0]">
									{{ cell }}
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- Pagination Buttons -->
				<div class="tables__pagination-buttons">
					<button class="icon-button" :disabled="wom.isFirstPage.value" @click="wom.firstPage()">
						<span class="material-symbols-outlined">first_page</span>
					</button>
					<button class="icon-button" :disabled="wom.isFirstPage.value" @click="wom.prevPage()">
						<span class="material-symbols-outlined">chevron_left</span>
					</button>
					<p>{{ wom.totalItems.value ? wom.pageStartIndex.value + 1 : 0 }} - {{ wom.pageEndIndex.value }} of {{ wom.totalItems.value }}</p>
					<button class="icon-button" :disabled="wom.isLastPage.value" @click="wom.nextPage()">
						<span class="material-symbols-outlined">chevron_right</span>
					</button>
					<button class="icon-button" :disabled="wom.isLastPage.value" @click="wom.lastPage()">
						<span class="material-symbols-outlined">last_page</span>
					</button>
				</div>
			</div>
		</div>

		<!-- Footer -->
		<div class="web-orders-view__footer">
			<div class="metrics">
				<p class="aliceblue fira">Count: {{ wom.filteredWebOrdersCount.value }}</p>
				<p class="aliceblue fira">Value: {{ formatCurrency(wom.filteredWebOrdersValue.value) }}</p>
			</div>
		</div>
	</div>

	<!-- Slide Out Modal -->
	<div
		v-if="wom.selectedWebOrder.value"
		class="slideOutModal-backdrop"
		@click="closeSlideOutModal"
		:style="{ cursor: isUIBusy ? 'wait' : 'default' }"
	>
		<div class="slideOutModal-container" @click.stop>
			<div class="webOrder__header">
				<p class="semi-large">
					Web Order:
					<a :href="'#'" @click.prevent>
						{{ wom.selectedWebOrder.value.id }}
					</a>
				</p>
			</div>
			<div class="webOrder__info">
				<p class="field-header">Customer:</p>
				<p>{{ wom.selectedWebOrder.value.company || wom.selectedWebOrder.value.customerName }}</p>
				<p class="field-header">Total Value:</p>
				<p>{{ formatCurrency(wom.selectedWebOrder.value.total) }}</p>
				<label for="baid" class="field-header">BAID:</label>
				<input id="baid" class="modal" v-model="baid" :disabled="isUIBusy" />
				<label for="po" class="field-header">PO Number:</label>
				<input id="po" class="modal" v-model="poNumber" :disabled="isUIBusy" />
				<label for="desc" class="field-header">Description:</label>
				<input id="desc" class="modal" v-model="salesOrderDescription" :disabled="isUIBusy" />
				<label for="notes" class="field-header">Qteedy Notes:</label>
				<textarea id="notes" disabled :value="wom.selectedWebOrder.value.notes ?? ''" :style="{ cursor: isUIBusy ? 'wait' : 'text' }" />
			</div>
			<p class="field-header fira">Items:</p>
			<div class="webOrder__items-table">
				<table class="items-table">
					<colgroup>
						<col style="width: 62%" />
						<col style="width: 15%" />
						<col style="width: 23%" />
					</colgroup>
					<thead>
						<tr>
							<th>Inventory ID</th>
							<th>Qty</th>
							<th>Disc (%)</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="item in wom.selectedWebOrder.value.lineItems" :key="item">
							<td>
								{{ item.inventoryId }}<br /><span class="secondary-text">{{ item.variantTitle }}</span>
							</td>
							<td>{{ item.quantity }}</td>
							<td>{{ Math.round(item.discountPercent * 10) / 10 }}</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div class="webOrder__actions">
				<button :disabled="isUIBusy" class="light" @click="createSO()">Create SO</button>
				<button :disabled="isUIBusy" class="light" @click="wom.clearSelectedWebOrder()">Close</button>
			</div>
		</div>
	</div>

	<!-- Login Modal -->
	<AcumaticaLoginModal
		v-if="displayLoginForm"
		:isUIBusy="isUIBusy"
		:loginOperation="loginOperation"
		@exit="closeLoginForm"
		@credentials="(username, password) => loginToAC(username, password)"
	/>
</template>

<style scoped>
/* Main Layout */
.web-orders-view {
	height: 100vh;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	user-select: none;
}

.web-orders-view__header {
	display: flex;
	justify-content: space-between;
	margin: 15px;
}

.web-orders-view__content {
	flex: 1;
	min-height: 0;
	display: flex;
	gap: 15px;
}

.web-orders-view__footer {
	display: flex;
	padding: 15px 0 15px 15px;
	gap: 15px;
}

.web-orders-view__tables {
	flex: 1;
	margin: 0 15px;
	display: flex;
	flex-direction: column;
}

.header__titles {
	h1 {
		font-weight: normal;
		margin: 0;
		font-size: 1.5rem;
	}

	p {
		color: rgb(149, 156, 161);
		font-size: 1.2rem;
	}
}

.tables__table-header {
	background: #4b525a;
	border-radius: 5px 5px 0 0;
	margin-top: 5px;
	overflow-y: auto;
	scrollbar-gutter: stable;
}

.tables__main-table {
	max-height: 100%;
	overflow-y: auto;
	background-color: #4b525a;
	flex: 1;
	scrollbar-gutter: stable;
}

/* Table */
table.main-table {
	width: 100%;
	border-collapse: collapse;
	background: transparent;
	font-family: 'Fira Sans', serif;
	flex: 1;
	table-layout: fixed;
}

table.main-table thead {
	border-bottom: 1px solid #272b2f;
	text-align: left;
	color: aliceblue;
	top: 0;
	position: sticky;
	opacity: 100;
}

table.main-table thead th {
	padding: 15px 15px;
	font-weight: normal;
}

table.main-table tbody {
	max-height: 100%;
	overflow-y: auto;
}

table.main-table tbody td {
	padding: 15px 15px;
	white-space: pre-line;
	font-weight: 200;
	color: aliceblue;
}

table.main-table tbody tr:hover {
	background: #646e79;
	cursor: pointer;
}

table.main-table tbody tr.selected {
	background: #646e79;
}

table.main-table tbody tr.disabled {
	opacity: 0.5;
	pointer-events: none;
}

/* Input */
input {
	width: 250px;
	font-family: 'Fira Sans';
	height: fit-content;
	border: #272b2f 2px solid;
	font-size: 0.75rem;
	padding: 2px 5px;
	border-radius: 5px;
	box-sizing: border-box;
	align-self: flex-end;
}

input:focus {
	border: #4b525a 2px solid;
	outline: none;
}

.metrics {
	display: flex;
	gap: 15px;
	align-items: center;

	p {
		color: aliceblue;
		font: 'Fira Sans';
		gap: 15px;
	}
}

.tables__pagination-buttons {
	background-color: #4b525a;
	border-radius: 0 0 5px 5px;
	display: flex;
	justify-content: flex-end;
	padding-right: 15px;
	border-top: 1px solid #272b2f;

	p {
		margin: 5px 5px 0 5px;
		color: rgb(189, 195, 201);
	}
}

/* Modal */
.slideOutModal-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: right;
	align-items: center;
	z-index: 1000;
}

.slideOutModal-container {
	background: #f5f5f5;
	width: 450px;
	height: 80vh;
	box-shadow: -2px 0 10px rgba(0, 0, 0, 0.3);
	display: flex;
	gap: 8px;
	flex-direction: column;
	padding: 20px;
	overflow-y: auto;
	border-radius: 8px 0 0 8px;
}

.webOrder__header {
	font-family: 'Fira Sans';
}

.webOrder__info {
	display: grid;
	grid-template-columns: max-content 1fr;
	column-gap: 15px;
	row-gap: 3px;
}

.webOrder__info p,
.webOrder__info label {
	font-family: 'Fira Sans';
	font-size: 0.85rem;
}

.field-header {
	font-weight: 500;
}

textarea {
	resize: none;
	height: 100px;
}

table.items-table {
	font-size: 0.85rem;
	table-layout: fixed;
	width: 100%;
	border: 1px solid #d3d3d3;
	border-radius: 3px;
	border-collapse: collapse;
	border-spacing: 0;
	font-family: 'Fira Sans';

	thead {
		border-bottom: 1px solid #d3d3d3;
		background-color: #e8e8e8;
	}

	thead th {
		padding: 3px 0 3px 5px;
		font-weight: normal;
		text-align: left;
	}

	tbody {
		max-height: 100%;
		overflow-y: auto;
		background-color: #fafafa;
	}

	tbody td {
		padding: 0 0 0 5px;
		white-space: pre-line;
		font-weight: 400;
	}

	.secondary-text {
		font-style: italic;
	}
}

.webOrder__items-table {
	overflow-y: auto;
}

.webOrder__actions,
.login-modal__actions {
	display: flex;
	justify-content: center;
	gap: 8px;
}

.webOrder__actions button:disabled {
	cursor: wait;
}

input:disabled {
	cursor: wait;
}
</style>
